"use client";

import type {
  PnrFormValues,
  HealthCheckResponse,
  SubmitErrorsLabels,
  PnrLocatorRequest as PnrRequest,
  PnrLocatorResponse as PnrResponse,
} from "@jb-manage-trips/types/pnr";

import type { ManageTripsRuntimeConfig } from "@jb-manage-trips/types/config";

import { generatePnrServiceRequestSchema } from "../../../../helpers/generate-pnr-service-request";
import { sixCharAlphanumeric } from "../../../../helpers/confirmation";
import {
  API_MOCK,
  buildSabreDxUrlShared,
  healthCheck as sharedHealthCheck,
  jsonFetch,
  shouldRedirectToQualifier,
} from "../../shared/services/common.client";

function displayErrorMessage(
  status: number | undefined,
  confirmationCode: string,
  submitErrors: SubmitErrorsLabels,
): string {
  if (status === 404) {
    return submitErrors.tripsNotFoundError;
  }

  if (status === 500) {
    return submitErrors.serviceError;
  }
  if (sixCharAlphanumeric.test(confirmationCode)) {
    return submitErrors.confirmationSubmitError;
  }
  return submitErrors.ticketNumberSubmitError;
}

/** Service factory — pure (no React hooks) */
export function createManageFlightsService(
  manageTrips: ManageTripsRuntimeConfig,
) {
  async function mybPnrService(
    requestBody: PnrRequest,
    signal?: AbortSignal,
  ): Promise<PnrResponse> {
    const {
      MANAGE_TRIPS: {
        API: { PNR_SERVICE_URL, PNR_SERVICE_API_KEY, PNR_SERVICE_APP_ID },
      },
    } = manageTrips;
    // eslint-disable-next-line no-implicit-coercion -- prefer this pattern
    const localHealthCheckUrl = !!process.env.NEXT_PUBLIC_LOCAL_PNR_HEALTH_URL;
    if (API_MOCK || localHealthCheckUrl) {
      const mock: PnrResponse = {
        b6PNRLocatorRS: {
          b6PNRRecordLocator: requestBody.b6PNRLocatorRQ.pnrRecordLocator,
        },
      };
      return new Promise((resolve) => {
        setTimeout(() => {
          resolve(mock);
        }, 200);
      });
    }

    return jsonFetch<PnrResponse>(PNR_SERVICE_URL, {
      body: requestBody,
      headers: {
        apikey: PNR_SERVICE_API_KEY,
        "app-id": PNR_SERVICE_APP_ID,
      },
      signal,
    });
  }

  async function doHealthCheck(
    b6PNRRecordLocator: string,
    lastName: string,
    signal?: AbortSignal,
  ): Promise<HealthCheckResponse> {
    const {
      MANAGE_TRIPS: {
        API: { HEALTH_CHECK_URL },
      },
    } = manageTrips;
    return sharedHealthCheck(
      HEALTH_CHECK_URL,
      { b6PNRRecordLocator, lastName },
      signal,
    );
  }

  async function getFullPnrCheckNonAuthenticated(
    formValues: PnrFormValues,
    signal?: AbortSignal,
    options?: { includeHealth?: boolean },
  ): Promise<{
    healthCheckResponse?: HealthCheckResponse;
    pnrServiceResponse: PnrResponse;
  }> {
    const includeHealth = options?.includeHealth ?? true;

    const requestSchema = generatePnrServiceRequestSchema({
      confirmationCode: formValues.confirmationCode,
      dob:
        typeof formValues.dateofBirth === "string"
          ? formValues.dateofBirth
          : "",
      lastName:
        typeof formValues.lastName === "string" ? formValues.lastName : "",
    });

    const pnrServiceResponse = await mybPnrService(requestSchema, signal);
    const b6PNRRecordLocator =
      pnrServiceResponse.b6PNRLocatorRS?.b6PNRRecordLocator ?? "";

    if (!includeHealth) {
      return { pnrServiceResponse };
    }

    const healthCheckResponse = await doHealthCheck(
      b6PNRRecordLocator,
      formValues.lastName,
      signal,
    );

    return { healthCheckResponse, pnrServiceResponse };
  }

  function buildSabreDxUrl(
    lastName: string,
    pnrRecordLocator: string,
    queryParameters?: Record<string, string | undefined>,
    platformOS?: "iOS" | "Android" | "Other",
  ): string {
    const {
      MANAGE_TRIPS: {
        API: { SABRE_DX_URL },
      },
    } = manageTrips;
    return buildSabreDxUrlShared(
      SABRE_DX_URL,
      lastName,
      pnrRecordLocator,
      queryParameters,
      platformOS,
    );
  }

  function getRedirectStatusToQualifier(
    emailExist: boolean,
    phoneNumberExist: boolean,
  ): boolean {
    const {
      MANAGE_TRIPS: {
        FLAGS: { SKIP_QUALIFIER_PHONE_MISSING: skipQualifierPhoneMissing },
      },
    } = manageTrips;
    return shouldRedirectToQualifier({
      emailExist,
      phoneNumberExist,
      skipQualifierPhoneMissing,
    });
  }

  return {
    buildSabreDxUrl,
    displayErrorMessage,
    getFullPnrCheckNonAuthenticated,
    getRedirectStatusToQualifier,
  };
}
